/**
 * Vanity org subdomains (TRA-829).
 *
 * <org>.trainloop.ai serves the same SPA pre-scoped to that org. The
 * subdomain is presentation only — access control stays in the API via
 * team membership (X-Team-Id validated against the JWT groups claim).
 */

import { IS_DEV } from '@/lib/env'

const BASE_DOMAIN = 'trainloop.ai'

// Infrastructure subdomains that are never org slugs.
// Static list; move to config.js if envs ever diverge.
const RESERVED_SUBDOMAINS = new Set([
  'platform',
  'platform-v2',
  'test',
  'www',
  'api',
  'api-test',
  'auth',
  'auth-test',
  'inference',
  'inference-test',
  'inference-internal',
  'dev',
  'api-dev',
  'auth-dev',
  'inference-dev',
])

/** Derive the vanity org slug from a hostname, or null if not a vanity host. */
export function deriveVanityOrgSlug(hostname: string): string | null {
  const suffix = `.${BASE_DOMAIN}`
  if (!hostname.endsWith(suffix)) {
    return null // localhost, previews, anything off-domain
  }
  const label = hostname.slice(0, -suffix.length)
  if (label.includes('.') || RESERVED_SUBDOMAINS.has(label) || label.length === 0) {
    return null // multi-level or infrastructure subdomain
  }
  return label
}

/**
 * Display form of an org slug: uppercase the first letter and leave the
 * rest verbatim ("protege" → "Protege"). nginx applies the same rule per
 * Host so link previews match the tab title — see the $org_initial map in
 * infra/pulumi-v2/components/apps/platform-v2-web.ts. A slug that starts
 * with a digit has no letter to raise and passes through unchanged.
 */
export function capitalizeOrgSlug(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1)
}

/**
 * Dev-only override: localhost has no vanity subdomain, so
 * `?vanity-org=nolla` previews that org's branding (logo, gate) exactly
 * as the real host would render it. Ignored outside dev builds.
 */
function devVanityOverride(): string | null {
  if (!IS_DEV || typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('vanity-org')
}

/** The org slug this page is locked to, or null on canonical/dev hosts. */
export const VANITY_ORG_SLUG: string | null =
  typeof window === 'undefined' // vitest runs in node — no window at import
    ? null
    : deriveVanityOrgSlug(window.location.hostname) ?? devVanityOverride()
